import { gql } from "@apollo/client";
import { MARKETING_GRAPH } from "./Queries";

export const REFRESH_GIAS_SCHOOL_DATA = gql`
  mutation RefreshGiasSchoolData {
    refreshGiasSchoolData {
      success
      message
      job {
        id
        status
        progress
        message
      }
    }
  }
`;

export const CREATE_WORKFLOW = gql`
  ${MARKETING_GRAPH}
  mutation CreateMarketingWorkflow($settings: WorkflowSettingsInput!) {
    createMarketingWorkflow(settings: $settings) {
      success
      errors {
        path
        code
        message
      }
      workflow {
        ...MarketingWorkflowFields
      }
    }
  }
`;

const ENRICHMENT_RESULT = gql`
  fragment OutreachEnrichmentResultFields on EnrichmentActionResult {
    success
    message
    resolvedCount
    errors {
      path
      code
      message
    }
    batch {
      id
      status
      counts
      job {
        id
        status
        progress
        message
      }
    }
    attempt {
      id
      status
      retryCount
    }
    contact {
      id
    }
  }
`;

export const LAUNCH_OUTREACH_ENRICHMENT = gql`
  ${ENRICHMENT_RESULT}
  mutation LaunchOutreachEnrichment(
    $schoolIds: [ID!]
    $schoolFilters: EnrichmentSchoolFiltersInput
    $excludedSchoolIds: [ID!]
    $subjectIds: [ID!]!
    $includePreviouslyEnriched: Boolean!
    $attemptEmailValidation: Boolean!
  ) {
    launchOutreachEnrichment(
      schoolIds: $schoolIds
      schoolFilters: $schoolFilters
      excludedSchoolIds: $excludedSchoolIds
      subjectIds: $subjectIds
      includePreviouslyEnriched: $includePreviouslyEnriched
      attemptEmailValidation: $attemptEmailValidation
    ) {
      ...OutreachEnrichmentResultFields
    }
  }
`;
export const VALIDATE_OUTREACH_CONTACTS = gql`
  mutation ValidateOutreachContacts($contactIds: [ID!]!) {
    validateOutreachContacts(contactIds: $contactIds) {
      success
      message
      requestedCount
      queuedCount
      skippedCount
      errors {
        path
        code
        message
      }
      job {
        id
        status
        progress
        message
      }
    }
  }
`;
export const CANCEL_OUTREACH_ENRICHMENT = gql`
  ${ENRICHMENT_RESULT}
  mutation CancelOutreachEnrichment($batchId: ID!) {
    cancelOutreachEnrichment(batchId: $batchId) {
      ...OutreachEnrichmentResultFields
    }
  }
`;
export const RETRY_OUTREACH_ENRICHMENT_ATTEMPT = gql`
  ${ENRICHMENT_RESULT}
  mutation RetryOutreachEnrichmentAttempt($attemptId: ID!) {
    retryOutreachEnrichmentAttempt(attemptId: $attemptId) {
      ...OutreachEnrichmentResultFields
    }
  }
`;
export const UPDATE_OUTREACH_CONTACT = gql`
  ${ENRICHMENT_RESULT}
  mutation UpdateOutreachContact(
    $contactId: ID!
    $firstName: String!
    $lastName: String!
    $jobTitle: String!
    $subjectIds: [ID!]!
  ) {
    updateOutreachContact(
      contactId: $contactId
      firstName: $firstName
      lastName: $lastName
      jobTitle: $jobTitle
      subjectIds: $subjectIds
    ) {
      ...OutreachEnrichmentResultFields
    }
  }
`;
export const UPDATE_WORKFLOW = gql`
  mutation UpdateMarketingWorkflow(
    $workflowId: ID!
    $settings: WorkflowSettingsInput!
  ) {
    updateMarketingWorkflow(workflowId: $workflowId, settings: $settings) {
      success
      errors {
        path
        code
        message
      }
      workflow {
        id
        isEnabled
        name
        description
        sendFirstEmailOnEnrollment
        sendWindowStart
        sendWindowEnd
        sendTimezone
        activeWeekdays
      }
    }
  }
`;
export const CREATE_DRAFT = gql`
  mutation CreateMarketingDraft($workflowId: ID!, $sourceVersionId: ID) {
    createMarketingDraft(
      workflowId: $workflowId
      sourceVersionId: $sourceVersionId
    ) {
      success
      errors {
        path
        code
        message
      }
      version {
        id
      }
    }
  }
`;
export const DELETE_DRAFT = gql`
  mutation DeleteMarketingDraft($versionId: ID!) {
    deleteMarketingDraft(versionId: $versionId) {
      success
      errors {
        path
        code
        message
      }
    }
  }
`;
export const SAVE_DRAFT = gql`
  mutation SaveMarketingDraft(
    $versionId: ID!
    $settings: WorkflowSettingsInput
    $stages: [StageInput!]!
    $transitions: [TransitionInput!]!
  ) {
    saveMarketingDraft(
      versionId: $versionId
      settings: $settings
      stages: $stages
      transitions: $transitions
    ) {
      success
      errors {
        path
        code
        message
      }
      version {
        id
      }
    }
  }
`;
export const VALIDATE_DRAFT = gql`
  mutation ValidateMarketingDraft($versionId: ID!) {
    validateMarketingDraft(versionId: $versionId) {
      success
      errors {
        path
        code
        message
      }
    }
  }
`;
export const PUBLISH_DRAFT = gql`
  mutation PublishMarketingDraft($versionId: ID!) {
    publishMarketingDraft(versionId: $versionId) {
      success
      errors {
        path
        code
        message
      }
      version {
        id
      }
    }
  }
`;
export const STOP_WORKFLOW_VERSION = gql`
  mutation StopMarketingWorkflowVersion(
    $versionId: ID!
    $allowNoDefault: Boolean
  ) {
    stopMarketingWorkflowVersion(
      versionId: $versionId
      allowNoDefault: $allowNoDefault
    ) {
      success
      outcome
      message
    }
  }
`;
export const RESTART_WORKFLOW_VERSION = gql`
  mutation RestartMarketingWorkflowVersion($versionId: ID!) {
    restartMarketingWorkflowVersion(versionId: $versionId) {
      success
      outcome
      message
    }
  }
`;
export const MIGRATE_PARTICIPANTS = gql`
  mutation MigrateMarketingParticipants(
    $sourceVersionId: ID!
    $destinationWorkflowId: ID!
  ) {
    migrateMarketingParticipants(
      sourceVersionId: $sourceVersionId
      destinationWorkflowId: $destinationWorkflowId
    ) {
      success
      message
      summary {
        considered
        eligible
        active
        completed
        enrolled
        alreadyEnrolled
        excluded
      }
    }
  }
`;
export const SET_WORKFLOW_SENDERS = gql`
  mutation SetMarketingWorkflowSenders($workflowId: ID!, $senderIds: [ID!]!) {
    setMarketingWorkflowSenders(
      workflowId: $workflowId
      senderIds: $senderIds
    ) {
      success
      errors {
        path
        code
        message
      }
    }
  }
`;
export const SET_SENDER_ALLOCATIONS = gql`
  mutation SetMarketingSenderAllocations(
    $senderId: ID!
    $allocations: [SenderAllocationInput!]!
  ) {
    setMarketingSenderAllocations(
      senderId: $senderId
      allocations: $allocations
    ) {
      success
      errors {
        path
        code
        message
      }
    }
  }
`;
export const PREVIEW_TEMPLATE = gql`
  mutation PreviewMarketingTemplate(
    $templateId: ID!
    $contactId: ID
    $userId: ID
    $useMock: Boolean!
    $senderId: ID
  ) {
    previewMarketingTemplate(
      templateId: $templateId
      contactId: $contactId
      userId: $userId
      useMock: $useMock
      senderId: $senderId
    ) {
      success
      subject
      bodyText
      bodyHtml
      ctaUrl
      message
    }
  }
`;
export const TEST_TEMPLATE = gql`
  mutation TestSendMarketingTemplate(
    $templateId: ID!
    $contactId: ID
    $userId: ID
    $recipient: String!
    $useMock: Boolean!
    $senderId: ID
  ) {
    testSendMarketingTemplate(
      templateId: $templateId
      contactId: $contactId
      userId: $userId
      recipient: $recipient
      useMock: $useMock
      senderId: $senderId
    ) {
      success
      subject
      bodyText
      bodyHtml
      ctaUrl
      message
    }
  }
`;
export const ENROLL_USER = gql`
  mutation EnrollMarketingUser($userId: ID!, $workflowId: ID!, $versionId: ID) {
    enrollMarketingUser(
      userId: $userId
      workflowId: $workflowId
      versionId: $versionId
    ) {
      success
      outcome
      message
      enrollment {
        id
        recipientEmail
        status
      }
    }
  }
`;
export const ENROLL_CONTACTS = gql`
  mutation EnrollMarketingContacts(
    $contactIds: [ID!]!
    $workflowId: ID!
    $versionId: ID
  ) {
    enrollMarketingContacts(
      contactIds: $contactIds
      workflowId: $workflowId
      versionId: $versionId
    ) {
      success
      requested
      enrolled
      alreadyEnrolled
      ineligible
      missing
      results {
        outcome
        message
        contact {
          id
          firstName
          lastName
          email
        }
        enrollment {
          id
          status
        }
      }
    }
  }
`;
export const STOP_ENROLLMENT = gql`
  mutation StopMarketingEnrollment($enrollmentId: ID!, $reason: String) {
    stopMarketingEnrollment(enrollmentId: $enrollmentId, reason: $reason) {
      success
      outcome
      message
      enrollment {
        id
        status
      }
    }
  }
`;
export const RETRY_DELIVERY = gql`
  mutation RetryMarketingDelivery($deliveryId: ID!) {
    retryMarketingDelivery(deliveryId: $deliveryId) {
      success
      outcome
      message
      delivery {
        id
        status
        retryable
        retryUnavailableReason
      }
    }
  }
`;
export const SET_MARKETING_AUTOMATION = gql`
  mutation SetMarketingAutomation($enabled: Boolean!, $reason: String) {
    setMarketingAutomation(enabled: $enabled, reason: $reason) {
      success
      changed
      message
      settings {
        isEnabled
        pausedAt
        pauseReason
        updatedAt
      }
    }
  }
`;
export const SAVE_SENDER = gql`
  mutation SaveMarketingSender(
    $senderId: ID
    $data: SenderInput!
    $allocations: [SenderAllocationInput!]
  ) {
    saveMarketingSender(
      senderId: $senderId
      data: $data
      allocations: $allocations
    ) {
      success
      errors {
        path
        code
        message
      }
      sender {
        id
        name
        displayName
        fromEmail
        replyToEmail
        isActive
        credentialsConfigured
        signatureDocument
        signatureImageUrl
        signatureImageWidth
        signatureImageLinkUrl
      }
    }
  }
`;
export const DELETE_SENDER = gql`
  mutation DeleteMarketingSender($senderId: ID!) {
    deleteMarketingSender(senderId: $senderId) {
      success
      message
    }
  }
`;
export const UPDATE_SENDER_SIGNATURE_IMAGE = gql`
  mutation UpdateMarketingSenderSignatureImage(
    $senderId: ID!
    $image: Upload
    $remove: Boolean!
  ) {
    updateMarketingSenderSignatureImage(
      senderId: $senderId
      image: $image
      remove: $remove
    ) {
      success
      errors {
        path
        code
        message
      }
      sender {
        id
        signatureDocument
        signatureImageUrl
        signatureImageWidth
        signatureImageLinkUrl
      }
    }
  }
`;
export const TEST_SENDER = gql`
  mutation TestMarketingSender($senderId: ID!, $recipient: String!) {
    testMarketingSender(senderId: $senderId, recipient: $recipient) {
      success
      message
    }
  }
`;

export const STOP_OUTREACH_CONTACT = gql`
  mutation ($contactId: ID!, $reason: String!) {
    stopOutreachContact(contactId: $contactId, reason: $reason) {
      success
      message
      outcome
    }
  }
`;

export const MANAGE_VERSION = gql`
  mutation ManageVersion($versionId: ID!, $action: String!) {
    manageMarketingVersion(versionId: $versionId, action: $action) {
      success
      message
      outcome
    }
  }
`;
export const CONTINUE_PARTICIPANTS = gql`
  mutation ContinueParticipants(
    $sourceVersionId: ID!
    $destinationVersionId: ID!
    $afterId: Int
  ) {
    continueMarketingParticipants(
      sourceVersionId: $sourceVersionId
      destinationVersionId: $destinationVersionId
      afterId: $afterId
    )
  }
`;

export const ENROLL_AUDIENCE = gql`
  mutation EnrollAudience(
    $workflowId: ID!
    $versionId: ID
    $userIds: [ID!]
    $schoolIds: [ID!]
  ) {
    enrollMarketingAudience(
      workflowId: $workflowId
      versionId: $versionId
      userIds: $userIds
      schoolIds: $schoolIds
    ) {
      result
    }
  }
`;
export const ENROLL_SCHOOL_ONBOARDING = gql`
  mutation EnrollSchoolOnboarding($schoolId: ID!) {
    enrollSchoolOnboarding(schoolId: $schoolId) {
      result
    }
  }
`;
